import { RenderItemFormSidebarPanelCtx } from "datocms-plugin-sdk";
import { Button, Canvas } from "datocms-react-ui";
import { StoryCardChatGPTAnswer, LocalizedString, LocalizedStructuredText, ArticleChatGPTAnswer } from "../../utils/common-types";
import StoryGeneratorProgress, { StoryGenerationProgress } from "../../components/StoryGeneratorProgress";
import { useState } from "react";
import { sendMessageToChatGPT } from "../../utils/chatgpt";
import ArticleTranslatorProgress from "../../components/ArticleTranslatorProgress/ArticleTranslatorProgress";
import useGQLQueryWithClient from "../../hooks/useGQLQueryWithClient";
import { GraphQLClient } from "graphql-hooks";

function getPluginSettings() {
    return `
        query PluginSettingsModel {
            pluginSettingsModel {
                _allTranslationInstructionsForChatgptLocales {  locale, value }
                translationInstructionsForChatgpt
            }
        }
    `;
}

type Props = {
    ctx: RenderItemFormSidebarPanelCtx,
};

type PromiseAndAction = {
    promise: Promise<any>,
    action: (a: any) => any
};

async function translateArticleFromEnglish(goalLocale: string, formValues: Record<string, unknown>, translationInstructionsForChatgpt: string, setField: (path: string, value: unknown) => Promise<void>, setProgress?: React.Dispatch<React.SetStateAction<Set<number>>>) {
    if (setProgress) setProgress(new Set<number>([-1]));

    const chatGPTPromisesAndActions: PromiseAndAction[] = [];

    const title = formValues.title as LocalizedString;
    const headline = formValues.headline as LocalizedString;
    const paragraph1 = formValues.paragraph1 as LocalizedStructuredText;
    const paragraph2 = formValues.paragraph2 as LocalizedStructuredText;
    const paragraph3 = formValues.paragraph3 as LocalizedStructuredText;
    const paragraph4 = formValues.paragraph4 as LocalizedStructuredText;

    title[goalLocale] = "";
    headline[goalLocale] = "";
    paragraph1[goalLocale] = [{ "type": "paragraph", "children": [{ "text": "" }] }];
    paragraph2[goalLocale] = [{ "type": "paragraph", "children": [{ "text": "" }] }];
    paragraph3[goalLocale] = [{ "type": "paragraph", "children": [{ "text": "" }] }];
    paragraph4[goalLocale] = [{ "type": "paragraph", "children": [{ "text": "" }] }];


    setField("title", title);
    setField("headline", headline);
    setField("paragraph1", paragraph1);
    setField("paragraph2", paragraph2);
    setField("paragraph3", paragraph3);
    setField("paragraph4", paragraph4);

    let prompt = `You are a translator for a friendly femtech app. You have to translate articles.
    I give you a JSON object where:
    title is plain text, headline is plain text, paragraphs are DatoCMS structured texts is DAST format.
    Your response must be the same JSON structure transtlated into "${goalLocale}" language code.

    {
        "title": "${title.en}",
        "headline": "${headline.en}"
    }

    Your answer must be a single JSON with exactly the same structure as I provided, translate only the title, the headline.

    ${translationInstructionsForChatgpt}
    `;

    sendMessageToChatGPT({ context: prompt, messages: [], jsonResponse: true }).then(answer => {
        title[goalLocale] = answer.title;
        headline[goalLocale] = answer.headline;
        setField("title", title);
        setField("headline", headline);
        if (setProgress) {
            setProgress((prevProgress) => {
                const newProgress = new Set(prevProgress);
                newProgress.add(1);
                return newProgress;
            });
        }
    });

    const paragraphs: { paragraph: any, name: string, state: number }[] = [
        { paragraph: paragraph1, name: "paragraph1", state: 2 },
        { paragraph: paragraph2, name: "paragraph2", state: 3 },
        { paragraph: paragraph3, name: "paragraph3", state: 4 },
        { paragraph: paragraph4, name: "paragraph4", state: 5 },
    ];

    for (const { paragraph, name, state } of paragraphs) {
        // Empty field: [{"type":"paragraph","children":[{"text":""}]}]
        if (paragraph.en.length == 1 &&
            paragraph.en[0].children.length == 1 &&
            paragraph.en[0].children[0].text.length == 0) {
            continue
        };
        prompt = `You are a translator for a friendly femtech app. You have to translate articles.
        I give you a JSON object where:
        title is plain text, headline is plain text, paragraphs are DatoCMS structured texts is DAST format.
        Your response must be the same JSON structure transtlated into "${goalLocale}" language code.

        ${translationInstructionsForChatgpt}

        {"content": ${JSON.stringify(paragraph.en)}}

        Your answer must be a single JSON with exactly the same structure as I provided, translate only the values of the text tags
        `;


        sendMessageToChatGPT({ context: prompt, messages: [], jsonResponse: true }).then(answer => {
            paragraph[goalLocale] = answer.content;
            setField(name, paragraph);
            if (setProgress) {
                setProgress((prevProgress) => {
                    const newProgress = new Set(prevProgress);
                    newProgress.add(state);
                    return newProgress;
                });
            }
        });
    }
}

function ArticleTranslatorSidePanel({ ctx }: Props) {
    const client = new GraphQLClient({
        url: "https://graphql.datocms.com/",
        headers: {
            "Authorization": `Bearer c2637bf4c3ca322961249cf1b65310`, // Todo use user token
            "X-Include-Drafts": "true",
        }
    });

    const { loading, error, data } = useGQLQueryWithClient(client, getPluginSettings());
    const [progress, setProgress] = useState(new Set<number>());

    if (loading) return <div>Loading the plugin settigs from Dato</div>
    if (error) return <div>Can't load Plugin configuration</div>

    const pluginConfig = ((data.data as any)?.pluginSettingsModel) as {
        translationInstructionsForChatgpt: string,
        _allTranslationInstructionsForChatgptLocales: { locale: string, value: string }[],

    };
    console.log("translationInstructionsForChatgpt")
    const englishTranslationInstructionsForChatgpt = pluginConfig.translationInstructionsForChatgpt as string;
    let translationInstructionsForChatgpt = englishTranslationInstructionsForChatgpt;

    console.log("pluginConfig._allTranslationInstructionsForChatgptLocales:", pluginConfig._allTranslationInstructionsForChatgptLocales)

    for (const { locale, value } of pluginConfig._allTranslationInstructionsForChatgptLocales) {
        if (locale == ctx.locale) {
            translationInstructionsForChatgpt = value;
            console.log("Used localized instruction");
            break;
        }
    }


    return <Canvas ctx={ctx}>

        <Button
            onClick={() => { translateArticleFromEnglish(ctx.locale, ctx.formValues, translationInstructionsForChatgpt, ctx.setFieldValue, setProgress) }}
            disabled={ctx.locale == "en"}
        >
            Translate Article from English
        </Button>

        <div className="progress">
            <ArticleTranslatorProgress state={progress} />
        </div>
    </Canvas>
};

export default ArticleTranslatorSidePanel;